import Handler from './handler'
import { EVENTS } from '@/lib/consts.ts'
import { getBaseProperties, getOwnerId } from '@/lib/analytics/helpers'
import { DEFAULT_PAYLOAD, USE_IDENTIFY, HANDLER_METHODS } from './consts.ts'
import { useCore } from '@/composables/core'
import { getAdvancedMatchingTraits } from '@/composables/analytics/advanced-matching'
import { redactRawPii } from '@/lib/analytics/advanced-matching/redact.ts'
import { isMarketingConsentGranted } from '@/lib/analytics/advanced-matching/consent.ts'
import type { AnalyticsEntity, AnalyticsEventPayload, SegmentAnalytics } from './types'
import { getWindowAnalyticsHandler } from './types'

export default class Segment extends Handler {
  handlerSendStack: AnalyticsEventPayload[]
  handlerIdentifyStack: AnalyticsEventPayload[]

  constructor (config: ConstructorParameters<typeof Handler>[0], manager: ConstructorParameters<typeof Handler>[1]) {
    super(config, manager)
    this.handlerSendStack = []
    this.handlerIdentifyStack = []
  }

  __initialize () {
    // GTM handler instance
    const handler = getWindowAnalyticsHandler<SegmentAnalytics>(this.config.name)

    // shortcut: if handler is ok
    if (handler && typeof handler.track === 'function') {
      // done
      return super.__initialize()
    }

    return true
  }

  send (event: AnalyticsEventPayload): void {
    const handlerStack = this.handlerSendStack
    const handler = getWindowAnalyticsHandler<SegmentAnalytics>(this.config.name)
    // track requires two pamameters so we need to split payload
    const { event: eventName, event_name, settings, [USE_IDENTIFY]: use_identify, ...eventProps } = event

    if (this.isFacebookPayloadBlockedByConsent(event)) {
      return
    }

    if (use_identify) {
      this.identify(event)
    }

    const trackProps = this.isFacebookRouted(event)
      ? { ...redactRawPii(eventProps), ...getAdvancedMatchingTraits() }
      : eventProps

    this.sendToExternalToolIfAllowed(
      () => handler?.track(String(eventName ?? event_name), trackProps, settings as Record<string, unknown> | undefined),
      event,
      handlerStack,
      HANDLER_METHODS.TRACK
    )

    handlerStack.push(event)
  }

  identify (event: AnalyticsEventPayload): void {
    const handlerStack = this.handlerIdentifyStack
    const handler = getWindowAnalyticsHandler<SegmentAnalytics>(this.config.name)

    // identify requires two pamameters so we need to split payload
    const {
      settings,
      userId,
      [USE_IDENTIFY]: _use_identify,
      event: _eventName,
      ...eventProps
    } = event

    if (this.isFacebookPayloadBlockedByConsent(event)) {
      return
    }

    // Remove business_admin_status from identify payload
    delete eventProps?.business_admin_status

    this.reset(typeof userId === 'string' ? userId : null)

    const identifyProps = this.isFacebookRouted(event)
      ? { ...redactRawPii(eventProps), ...getAdvancedMatchingTraits() }
      : eventProps

    this.sendToExternalToolIfAllowed(
      () => handler?.identify(String(userId), identifyProps, settings as Record<string, unknown> | undefined),
      event,
      handlerStack,
      HANDLER_METHODS.IDENTIFY
    )

    handlerStack.push(event)
  }

  // Not Facebook-routed today (no `Facebook Pixel` integration flag below), so
  // isFacebookPayloadBlockedByConsent() is a no-op here; identify() still carries the
  // guard structurally in case this ever becomes FB-routed.
  [EVENTS.BUSINESS_SWITCHED] ({ country, routingConfig, business }: {
    country: string
    routingConfig: AnalyticsEntity
    business: AnalyticsEntity
  }) {
    const userId = getOwnerId({ business, country, routingConfig })
    const { locale } = useCore().i18n as unknown as { locale: string }

    this.identify({
      event_name: EVENTS.BUSINESS_SWITCHED,
      userId,
      settings: {
        context: {
          locale,
        },
      },
      locale,
    })

    return this.sendingSkipped
  }

  reset (userId: string | null | undefined): void {
    const handler = getWindowAnalyticsHandler<SegmentAnalytics>(this.config.name)
    const savedUserId = this.getSavedUserId()

    // Reset only if user id exists and is not the same.
    // It's important to keep the same asynchronous id for not logged user.
    if (savedUserId && savedUserId !== userId) {
      handler?.reset()
    }
  }

  getSavedUserId () {
    const handler = getWindowAnalyticsHandler<SegmentAnalytics>(this.config.name)

    try {
      return handler?.user().id()
    } catch (e) {
      return null
    }
  }

  getFacebookSettings () {
    return {
      integrations: {
        'All': false,
        'Facebook Pixel': true,
        'Facebook Conversions API (Actions)': true,
      },
    }
  }

  // Detects payloads routed to the Facebook Pixel/CAPI destination via `getFacebookSettings()`,
  // so raw PII can be redacted and hashed Advanced Matching traits attached only there.
  isFacebookRouted (event: AnalyticsEventPayload): boolean {
    const settings = event?.settings as { integrations?: Record<string, unknown> } | undefined
    return settings?.integrations?.['Facebook Pixel'] === true
  }

  // Facebook-routed payloads require Cookiebot `marketing` consent (mirrors mobile: no FB
  // events/Advanced Matching pre-consent). Checked in both send() (track) and identify() so
  // it also covers send()'s internal use_identify -> identify() call and BUSINESS_SWITCHED's
  // direct identify() call; non-Facebook payloads are never gated.
  isFacebookPayloadBlockedByConsent (event: AnalyticsEventPayload): boolean {
    return this.isFacebookRouted(event) && !isMarketingConsentGranted()
  }

  [DEFAULT_PAYLOAD] (data: Record<string, unknown>) {
    const baseProperties = getBaseProperties(data as never)
    const { locale } = useCore().i18n as unknown as { locale: string }
    const {
      app_version,
      business_category,
      business_id,
      business_phone,
      country,
      device_type,
      email,
      event_id,
      logged_in_user_id,
      user_role,
      user_id,
      user_type,
    } = baseProperties

    return {
      app_version,
      business_category,
      business_id,
      business_phone,
      country,
      device_type,
      email,
      event_id,
      logged_in_user_id,
      user_role,
      user_type,
      userId: user_id,
      settings: {
        context: {
          locale,
        },
      },
      locale,
    }
  }
}
